import { MeasurementPresentation } from "../../services/programInsights.service";
import {
	ArrangedEntryState,
	AuthorEntry,
	AuthorView,
	ProgramFigure,
	PutArrangedEntry,
	PutArrangement,
} from "../../services/arrangements.service";

/**
 * SIMS-8555 — the arrangement being edited, as pure functions over plain data.
 *
 * The screen holds an `Arrangement` and replaces it through these functions; it is
 * saved with `toPut`. The server enforces every rule again on save; the checks
 * here only stop the author sending what it would refuse.
 *
 * An entry keeps the choice it was read with until the author changes it. A stored
 * presentation that is no longer offered (AC9) or an entry whose measurement is
 * gone (AC8) is carried over unchanged, which the server accepts: both are
 * offered a repair, never forced into one.
 */

export const SECTION_NAME_MAX = 80;

export interface EditEntry {
	key: string;
	eventId: string;
	/** The measurement's id (AC16); null on an entry stored before ids, which can't be sent back. */
	measurementId: string | null;
	name: string | null;
	eventName: string | null;
	/** As read: what the server resolved this entry to. */
	state: ArrangedEntryState;
	presentation: MeasurementPresentation | null;
	summary: MeasurementPresentation | null;
	resultId: string | null;
	/** What the entry was read with; null when it was not in the arrangement. */
	read: {
		presentation: MeasurementPresentation;
		summary: MeasurementPresentation | null;
		resultId: string | null;
	} | null;
	source: AuthorEntry;
}

export interface EditSection {
	id: string;
	name: string;
	entries: EditEntry[];
}

export interface Arrangement {
	/** SIMS-8562: the arrangement edited; null for the draft of a surface never arranged. */
	id: string | null;
	/** Null: never named (SIMS-8555's first save) and left so; not sent. */
	name: string | null;
	/** The account's roles it is for; empty: every role. */
	roleIds: string[];
	sections: EditSection[];
	/** Declarations not placed in any section, in the order they were read. */
	unarranged: EditEntry[];
	figures: ProgramFigure[];
	available: ProgramFigure[];
	/** Where each entry was read, so one returned to "Not yet arranged" keeps its place. */
	readOrder: Record<string, number>;
}

const toEdit = (e: AuthorEntry, placed: boolean): EditEntry => {
	const chosen = placed && e.stored ? e.stored : null;
	return {
		key: e.key,
		eventId: e.eventId,
		measurementId: e.measurementId,
		name: e.name,
		eventName: e.eventName,
		state: e.state,
		presentation: chosen ? chosen.presentation : e.presentation,
		summary: chosen ? chosen.summary : null,
		resultId: chosen ? chosen.resultId : null,
		read: chosen,
		source: e,
	};
};

/** The screen's starting point: a stored arrangement as read, or the draft (AC1). */
export const fromView = (view: AuthorView): Arrangement => {
	const all = [...view.sections.flatMap((s) => s.entries), ...view.unarranged];
	return {
		id: view.id,
		name: view.name,
		roleIds: [...view.roleIds],
		sections: view.sections.map((s) => ({
			id: s.id,
			name: s.name,
			entries: s.entries.map((e) => toEdit(e, true)),
		})),
		unarranged: view.unarranged.map((e) => toEdit(e, false)),
		figures: [...view.figures.arranged],
		available: [...view.figures.available],
		readOrder: Object.fromEntries(all.map((e, i) => [e.key, i])),
	};
};

// ── What each entry may carry ────────────────────────────────────────────────

export const offeredSummaries = (e: EditEntry): MeasurementPresentation[] =>
	e.presentation === "TREND" ? e.source.trend.summaries : [];

export const offeredResults = (e: EditEntry): AuthorEntry["results"] =>
	e.presentation === "TREND" ? e.source.trend.results : [];

export const isOffered = (
	e: EditEntry,
	presentation: MeasurementPresentation | null,
): boolean =>
	presentation !== null &&
	e.source.presentations.some(
		(o) => o.presentation === presentation && o.offered,
	);

/** Exactly as it was read: the server keeps such an entry even when it has gone stale. */
export const isUnchanged = (e: EditEntry): boolean =>
	e.read !== null &&
	e.read.presentation === e.presentation &&
	e.read.summary === e.summary &&
	e.read.resultId === e.resultId;

/** Why this entry would not draw as chosen; empty when it would. */
export interface EntryIssues {
	orphaned: boolean;
	noShape: boolean;
	/** AC9: the chosen presentation is no longer offered; the dashboard uses the default. */
	invalidPresentation: boolean;
	invalidSummary: boolean;
	invalidResult: boolean;
}

export const issuesOf = (e: EditEntry): EntryIssues => {
	const orphaned = e.state === "ORPHANED";
	const noShape = e.state === "NO_SHAPE";
	const live = !orphaned && !noShape;
	return {
		orphaned,
		noShape,
		invalidPresentation: live && !isOffered(e, e.presentation),
		invalidSummary:
			live && e.summary !== null && !offeredSummaries(e).includes(e.summary),
		invalidResult:
			live &&
			e.resultId !== null &&
			!offeredResults(e).some((r) => r.id === e.resultId),
	};
};

// ── Editing ───────────────────────────────────────────────────────────────────

const mapEntry = (
	a: Arrangement,
	key: string,
	change: (e: EditEntry) => EditEntry,
): Arrangement => ({
	...a,
	sections: a.sections.map((s) => ({
		...s,
		entries: s.entries.map((e) => (e.key === key ? change(e) : e)),
	})),
});

/** AC4: only an offered presentation can be chosen. A summary or limit it can't carry is dropped. */
export const choosePresentation = (
	a: Arrangement,
	key: string,
	presentation: MeasurementPresentation,
): Arrangement =>
	mapEntry(a, key, (e) => {
		if (!isOffered(e, presentation)) return e;
		const next = { ...e, presentation };
		return {
			...next,
			summary:
				e.summary !== null && offeredSummaries(next).includes(e.summary)
					? e.summary
					: null,
			resultId:
				e.resultId !== null &&
				offeredResults(next).some((r) => r.id === e.resultId)
					? e.resultId
					: null,
		};
	});

/** AC12: a summary offered for the trend, or none. */
export const chooseSummary = (
	a: Arrangement,
	key: string,
	summary: MeasurementPresentation | null,
): Arrangement =>
	mapEntry(a, key, (e) =>
		summary === null || offeredSummaries(e).includes(summary)
			? { ...e, summary }
			: e,
	);

/** AC13: one of the event's configured results, or every occurrence. */
export const chooseResult = (
	a: Arrangement,
	key: string,
	resultId: string | null,
): Arrangement =>
	mapEntry(a, key, (e) =>
		resultId === null || offeredResults(e).some((r) => r.id === resultId)
			? { ...e, resultId }
			: e,
	);

const byReadOrder =
	(a: Arrangement) =>
	(x: EditEntry, y: EditEntry): number =>
		(a.readOrder[x.key] ?? Number.MAX_SAFE_INTEGER) -
		(a.readOrder[y.key] ?? Number.MAX_SAFE_INTEGER);

/** Hex id the server accepts for a section not yet saved (`draft-` + 24 hex). */
export const newSectionId = (): string =>
	`draft-${Array.from({ length: 24 }, () =>
		Math.floor(Math.random() * 16).toString(16),
	).join("")}`;

export const addSection = (
	a: Arrangement,
	name: string,
	id: string = newSectionId(),
): Arrangement => ({
	...a,
	sections: [...a.sections, { id, name, entries: [] }],
});

export const renameSection = (
	a: Arrangement,
	id: string,
	name: string,
): Arrangement => ({
	...a,
	sections: a.sections.map((s) => (s.id === id ? { ...s, name } : s)),
});

/**
 * A removed section's measurements go back to "Not yet arranged", where the
 * dashboard still draws them by event. An orphan has nowhere to go and is dropped.
 */
export const removeSection = (a: Arrangement, id: string): Arrangement => {
	const gone = a.sections.find((s) => s.id === id);
	if (!gone) return a;
	return {
		...a,
		sections: a.sections.filter((s) => s.id !== id),
		unarranged: [
			...a.unarranged,
			...gone.entries.filter((e) => e.state !== "ORPHANED").map(unplace),
		].sort(byReadOrder(a)),
	};
};

/** Back to "Not yet arranged": no longer an arranged choice, so its default again. */
const unplace = (e: EditEntry): EditEntry => ({
	...e,
	presentation: e.source.defaultPresentation,
	summary: null,
	resultId: null,
	read: null,
	state: e.state === "INVALID_PRESENTATION" ? "OK" : e.state,
});

const move = <T>(list: T[], from: number, to: number): T[] => {
	if (
		from === to ||
		from < 0 ||
		to < 0 ||
		from >= list.length ||
		to >= list.length
	)
		return list;
	const next = [...list];
	const [item] = next.splice(from, 1);
	next.splice(to, 0, item);
	return next;
};

/** AC3: sections in any order. */
export const moveSection = (
	a: Arrangement,
	from: number,
	to: number,
): Arrangement => ({ ...a, sections: move(a.sections, from, to) });

/** AC3: measurements in any order within their section. */
export const moveEntry = (
	a: Arrangement,
	sectionId: string,
	from: number,
	to: number,
): Arrangement => ({
	...a,
	sections: a.sections.map((s) =>
		s.id === sectionId ? { ...s, entries: move(s.entries, from, to) } : s,
	),
});

/** AC8's repair, and the first half of moving a measurement to another section. */
export const removeEntry = (
	a: Arrangement,
	sectionId: string,
	key: string,
): Arrangement => {
	const section = a.sections.find((s) => s.id === sectionId);
	const entry = section?.entries.find((e) => e.key === key);
	if (!entry) return a;
	return {
		...a,
		sections: a.sections.map((s) =>
			s.id === sectionId
				? { ...s, entries: s.entries.filter((e) => e.key !== key) }
				: s,
		),
		unarranged:
			entry.state === "ORPHANED"
				? a.unarranged
				: [...a.unarranged, unplace(entry)].sort(byReadOrder(a)),
	};
};

/** "Add measurement to this section" (8b): from "Not yet arranged", at its default. */
export const addEntry = (
	a: Arrangement,
	sectionId: string,
	key: string,
): Arrangement => {
	const entry = a.unarranged.find((e) => e.key === key);
	if (!entry || entry.state === "NO_SHAPE" || entry.presentation === null) {
		return a;
	}
	return {
		...a,
		unarranged: a.unarranged.filter((e) => e.key !== key),
		sections: a.sections.map((s) =>
			s.id === sectionId ? { ...s, entries: [...s.entries, entry] } : s,
		),
	};
};

/** AC5: a program-wide figure removed from the surface... */
export const removeFigure = (
	a: Arrangement,
	figure: ProgramFigure,
): Arrangement => ({ ...a, figures: a.figures.filter((f) => f !== figure) });

/** ...and added back; AC15 limits the participant surface to what it offers. */
export const addFigure = (
	a: Arrangement,
	figure: ProgramFigure,
): Arrangement =>
	a.available.includes(figure) && !a.figures.includes(figure)
		? { ...a, figures: [...a.figures, figure] }
		: a;

export const removableFigures = (a: Arrangement): ProgramFigure[] =>
	a.available.filter((f) => !a.figures.includes(f));

// ── Checks and saving ─────────────────────────────────────────────────────────

/** Section-name problems the server would refuse (C17300), by section id. */
export const sectionProblems = (a: Arrangement): Record<string, string> => {
	const out: Record<string, string> = {};
	const seen = new Set<string>();
	for (const s of a.sections) {
		const name = s.name.trim();
		const folded = name.toLowerCase();
		if (!name) out[s.id] = "A section needs a name.";
		else if (name.length > SECTION_NAME_MAX) {
			out[s.id] = `A name is at most ${SECTION_NAME_MAX} characters.`;
		} else if (seen.has(folded)) {
			out[s.id] = "Another section already has this name.";
		}
		seen.add(folded);
	}
	return out;
};

/**
 * An entry the server would refuse: its presentation is no longer offered and the
 * author changed something else on it, so it is no longer carried over unchanged.
 */
export const entryNeedsRepair = (e: EditEntry): boolean => {
	if (isUnchanged(e)) return false;
	const issues = issuesOf(e);
	return (
		issues.invalidPresentation || issues.invalidSummary || issues.invalidResult
	);
};

/** SIMS-8562 AC1: a name, once given, can't be emptied; at most 80 characters. */
export const ARRANGEMENT_NAME_MAX = 80;

export const nameProblem = (a: Arrangement): string | null => {
	if (a.name === null) return null;
	const name = a.name.trim();
	if (!name) return "An arrangement needs a name.";
	if (name.length > ARRANGEMENT_NAME_MAX) {
		return `An arrangement's name is at most ${ARRANGEMENT_NAME_MAX} characters.`;
	}
	return null;
};

export const rename = (a: Arrangement, name: string): Arrangement => ({
	...a,
	name,
});

/** SIMS-8562 AC2: the roles it is for; empty makes it the default for every role. */
export const restrictTo = (a: Arrangement, roleIds: string[]): Arrangement => ({
	...a,
	roleIds: [...roleIds],
});

export const canSave = (a: Arrangement): boolean =>
	nameProblem(a) === null &&
	Object.keys(sectionProblems(a)).length === 0 &&
	a.sections.every((s) => s.entries.every((e) => !entryNeedsRepair(e)));

/**
 * An entry as saved, by its measurement's id. One that names no measurement (stored
 * before AC16) can't be sent back: it is shown as no longer available and dropped
 * on save, which the screen says on its row.
 */
const putEntry = (e: EditEntry): PutArrangedEntry | null => {
	if (e.presentation === null || e.measurementId === null) return null;
	return {
		eventId: e.eventId,
		measurementId: e.measurementId,
		presentation: e.presentation,
		...(e.summary ? { summary: e.summary } : {}),
		...(e.resultId ? { resultId: e.resultId } : {}),
	};
};

export const toPut = (a: Arrangement): PutArrangement => ({
	sections: a.sections.map((s) => ({
		id: s.id,
		name: s.name.trim(),
		entries: s.entries
			.map(putEntry)
			.filter((e): e is PutArrangedEntry => e !== null),
	})),
	figures: [...a.figures],
	// SIMS-8562: a stored arrangement is saved by its id, with who it is for.
	...(a.id
		? {
				id: a.id,
				roleIds: [...a.roleIds],
				...(a.name !== null ? { name: a.name.trim() } : {}),
			}
		: {}),
});

/** Unsaved changes: what would be sent differs from what was read. */
export const isDirty = (a: Arrangement, read: Arrangement): boolean =>
	JSON.stringify(toPut(a)) !== JSON.stringify(toPut(read));

/** Counts for 8f's summary line (AC8: the entries no longer available). */
export const countsOf = (
	view: AuthorView,
): {
	sections: number;
	measurements: number;
	unavailable: number;
	declared: number;
	events: number;
} => {
	const placed = view.sections.flatMap((s) => s.entries);
	const live = [...placed, ...view.unarranged].filter(
		(e) => e.state !== "ORPHANED",
	);
	return {
		sections: view.sections.length,
		measurements: placed.filter((e) => e.state !== "ORPHANED").length,
		unavailable: placed.filter((e) => e.state === "ORPHANED").length,
		declared: live.length,
		events: new Set(live.map((e) => e.eventId)).size,
	};
};
